/**
 * 用户等级系统工具函数
 * 基于累计充值金额计算用户等级和存储空间
 */

export interface UserLevel {
  level: number;
  name: string;
  minRecharge: number; // 累计充值金额（元）
  maxRecharge: number;
  storageBytes: number;
  dailyFreeQuota: {
    standard: number;
    hd: number;
  };
}

export const USER_LEVELS: UserLevel[] = [
  { 
    level: 1, 
    name: '产品助理', 
    minRecharge: 0, 
    maxRecharge: 9.99, 
    storageBytes: 10 * 1024 * 1024, // 10 MB
    dailyFreeQuota: { standard: 1, hd: 0 }
  },
  { 
    level: 2, 
    name: '初级产品经理', 
    minRecharge: 10, 
    maxRecharge: 49.99, 
    storageBytes: 50 * 1024 * 1024, // 50 MB
    dailyFreeQuota: { standard: 2, hd: 0 }
  },
  { 
    level: 3, 
    name: '中级产品经理', 
    minRecharge: 50, 
    maxRecharge: 99.99, 
    storageBytes: 200 * 1024 * 1024, // 200 MB
    dailyFreeQuota: { standard: 3, hd: 0 }
  },
  { 
    level: 4, 
    name: '高级产品经理', 
    minRecharge: 100, 
    maxRecharge: 199.99, 
    storageBytes: 1024 * 1024 * 1024, // 1 GB
    dailyFreeQuota: { standard: 5, hd: 0 }
  },
  { 
    level: 5, 
    name: '资深产品经理', 
    minRecharge: 200, 
    maxRecharge: 499.99, 
    storageBytes: 5 * 1024 * 1024 * 1024, // 5 GB
    dailyFreeQuota: { standard: 10, hd: 0 }
  },
  { 
    level: 6, 
    name: '产品专家', 
    minRecharge: 500, 
    maxRecharge: 999.99, 
    storageBytes: 20 * 1024 * 1024 * 1024, // 20 GB
    dailyFreeQuota: { standard: 20, hd: 0 }
  },
  { 
    level: 7, 
    name: '产品总监', 
    minRecharge: 1000, 
    maxRecharge: 1999.99, 
    storageBytes: 50 * 1024 * 1024 * 1024, // 50 GB
    dailyFreeQuota: { standard: 50, hd: 0 }
  },
  { 
    level: 8, 
    name: '首席产品官', 
    minRecharge: 2000, 
    maxRecharge: Infinity, 
    storageBytes: 100 * 1024 * 1024 * 1024, // 100 GB
    dailyFreeQuota: { standard: 100, hd: 0 }
  },
];

/**
 * 根据累计充值金额计算用户等级
 */
export function calculateUserLevel(totalRecharge: number): UserLevel {
  for (let i = USER_LEVELS.length - 1; i >= 0; i--) {
    if (totalRecharge >= USER_LEVELS[i].minRecharge) {
      return USER_LEVELS[i];
    }
  }
  return USER_LEVELS[0];
}

/**
 * 等级样式配置
 */
export const LEVEL_STYLES: Record<number, {
  color: string;
  backgroundColor: string;
  borderColor: string;
  icon: string;
  glowColor: string;
}> = {
  1: { color: '#52c41a', backgroundColor: '#f6ffed', borderColor: '#b7eb8f', icon: '🌱', glowColor: 'rgba(82, 196, 26, 0.4)' }, // 入门 - 绿色 幼苗
  2: { color: '#1890ff', backgroundColor: '#e6f7ff', borderColor: '#91d5ff', icon: '📖', glowColor: 'rgba(24, 144, 255, 0.4)' }, // 初级 - 蓝色 书本
  3: { color: '#722ed1', backgroundColor: '#f9f0ff', borderColor: '#d3adf7', icon: '📊', glowColor: 'rgba(114, 46, 209, 0.4)' }, // 中级 - 紫色 图表
  4: { color: '#fa8c16', backgroundColor: '#fff7e6', borderColor: '#ffd591', icon: '⭐', glowColor: 'rgba(250, 140, 22, 0.4)' }, // 高级 - 橙色 星星
  5: { color: '#f5222d', backgroundColor: '#fff1f0', borderColor: '#ffa39e', icon: '🎖️', glowColor: 'rgba(245, 34, 45, 0.4)' }, // 资深 - 红色 勋章
  6: { color: '#13c2c2', backgroundColor: '#e6fffb', borderColor: '#87e8de', icon: '🛡️', glowColor: 'rgba(19, 194, 194, 0.4)' }, // 专家 - 青色 盾牌
  7: { color: '#faad14', backgroundColor: '#fffbe6', borderColor: '#ffe58f', icon: '👑', glowColor: 'rgba(250, 173, 20, 0.4)' }, // 总监 - 金色 皇冠
  8: { color: '#531dab', backgroundColor: '#f9f0ff', borderColor: '#d3adf7', icon: '👑', glowColor: 'rgba(83, 29, 171, 0.4)' }, // 首席 - 紫金色 皇冠
};

/**
 * 获取等级样式
 */
export function getLevelStyle(level: number) {
  return LEVEL_STYLES[level] || LEVEL_STYLES[1];
}

/**
 * 根据累计充值金额计算存储空间
 */
export function calculateStorageByRecharge(totalRecharge: number): number {
  const level = calculateUserLevel(totalRecharge);
  return level.storageBytes;
}

/**
 * 获取每日免费额度
 */
export function getDailyFreeQuota(totalRecharge: number): { standard: number; hd: number } {
  const level = calculateUserLevel(totalRecharge);
  return level.dailyFreeQuota;
}

/**
 * 获取下一级信息
 */
export function getNextLevel(totalRecharge: number): { level: number; name: string; requiredAmount: number } | null {
  const currentLevel = calculateUserLevel(totalRecharge);
  const nextLevelIndex = currentLevel.level;
  
  if (nextLevelIndex >= USER_LEVELS.length) {
    return null; // 已经是最高等级
  }
  
  const nextLevel = USER_LEVELS[nextLevelIndex];
  return {
    level: nextLevel.level,
    name: nextLevel.name,
    requiredAmount: Math.max(0, nextLevel.minRecharge - totalRecharge),
  };
}

/**
 * 计算当前等级进度百分比
 */
export function calculateLevelProgress(totalRecharge: number): number {
  // 负数金额返回 0%
  if (totalRecharge < 0) {
    return 0;
  }

  const currentLevel = calculateUserLevel(totalRecharge);

  if (currentLevel.level === USER_LEVELS.length) {
    return 100; // 最高等级
  }

  const range = currentLevel.maxRecharge - currentLevel.minRecharge + 0.01;
  const progress = totalRecharge - currentLevel.minRecharge;
  return Math.min(Math.round((progress / range) * 100), 100);
}

/**
 * 格式化存储空间大小
 */
export function formatStorageSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(0) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
}

/**
 * 获取等级描述
 */
export function getLevelDescription(level: number): string {
  const descriptions: Record<number, string> = {
    1: '刚刚起步的产品新人',
    2: '正在成长的产品新手',
    3: '具备一定经验的产品经理',
    4: '能够独立负责项目的高级PM',
    5: '经验丰富，能指导他人的资深PM',
    6: '在某一领域有深入研究的专家',
    7: '管理产品团队的产品总监',
    8: '统领产品战略的首席产品官',
  };
  return descriptions[level] || '';
}

/**
 * 获取等级权益列表
 */
export function getLevelBenefits(level: number): string[] {
  const userLevel = USER_LEVELS.find(l => l.level === level);
  if (!userLevel) return [];
  
  const benefits = [
    `存储空间：${formatStorageSize(userLevel.storageBytes)}`,
    `每日免费生成：${userLevel.dailyFreeQuota.standard}张标准图`,
  ];
  
  if (level >= 2) {
    benefits.push('解锁高级模板库');
  }
  if (level >= 3) {
    benefits.push('解锁场景图生成功能');
  }
  if (level >= 4) {
    benefits.push('优先客服支持');
  }
  if (level >= 5) {
    benefits.push('专属功能内测资格');
  }
  if (level >= 6) {
    benefits.push('定制化服务支持');
  }
  if (level >= 7) {
    benefits.push('团队管理功能');
  }
  if (level >= 8) {
    benefits.push('企业级专属服务');
  }
  
  return benefits;
}
